/**
 * Reservations by Channel Chart Component
 * Based on: documentation/cursor/owner-dashboard-ui-specification-v1-final.md
 * Section 4, Component 8: Reservations by Channel Chart
 */

import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import ChartTooltipPortal from './ChartTooltipPortal.jsx';
import ChartAnnotations from './ChartAnnotations.jsx';
import ChartInfoIcon from './analytics/ChartInfoIcon.jsx';
import { transformReservationsData } from '../../utils/analyticsTransform.js';
import { getChannelColorVar } from '../../utils/channelColorUtils.js';
import '../../styles/chart-card.css';
import '../../styles/charts.css';

export default function ReservationsChart({ data, channels, onSegmentClick, selectedChannels = [], annotations = [], periodReservations, isMobile = false }) {
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // ResizeObserver-based sizing to eliminate Recharts warnings
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setContainerSize({ width, height });
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <h3 style={styles.title}># of Reservations by Channel</h3>
          <p style={styles.subtitle}>Reservation volume by booking source</p>
        </div>
        <div style={styles.emptyState}>
          No reservation data available
        </div>
      </div>
    );
  }

  // Backend already filters by channel and date range (Reporting Period is authoritative)
  // Transform row-form data to chart-ready pivoted format
  const chartData = transformReservationsData(data, channels || []);

  // Get channel names and colors (use shared strict resolver - single source of truth)
  // Do NOT use backend color_hex to prevent drift
  const allChannelNames = (channels || []).map(c => c.name);
  const channelColors = {};
  (channels || []).forEach(channel => {
    const channelKey = channel.master_channel_key || channel.code;
    channelColors[channel.name] = channelKey ? getChannelColorVar(channelKey) : 'var(--color-text-tertiary, #737373)';
  });

  // Only show channels with data (non-zero in current view). Legend and tooltip reflect active series only.
  const activeChannels = allChannelNames.filter((name) =>
    chartData.some((d) => (d[name] || 0) > 0)
  );

  const chartDataWithMeta = chartData.map((d) => ({ ...d, _zero: 1e-9 }));
  const formatTotalLabel = (v) => {
    if (v == null || Number(v) <= 0) return '';
    return String(Math.round(Number(v)));
  };

  // Theme-aware colors (Recommendation 16: Customize Chart Styling)
  const gridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'; /* Recommendation 16: opacity 0.08 */
  const textColor = theme === 'dark' ? 'var(--color-text-secondary)' : '#525252'; /* Recommendation 16: refined gray */
  const tooltipBg = theme === 'dark' ? 'var(--color-bg-primary)' : '#fff';
  const tooltipBorder = theme === 'dark' ? 'var(--color-border-medium)' : '#e2e8f0';

  const handleBarClick = (data) => {
    if (onSegmentClick && data) {
      const monthDate = data.monthDate || new Date();
      onSegmentClick({
        type: 'reservations',
        month: monthDate,
        monthLabel: data.month,
        data: data,
      });
    }
  };

  return (
    <div className="chart-card" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h3 style={styles.title}>
            # of Reservations by Channel
            {periodReservations !== undefined && (
              <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280', marginLeft: '8px' }}>
                (Period Reservations: {new Intl.NumberFormat('en-US').format(periodReservations)})
              </span>
            )}
          </h3>
          <ChartInfoIcon
            title="Reservations by Channel"
            definition="Number of reservations in the selected period, broken out by channel. Each bar is a month; segments are channels."
            limitations="Each reservation counts once in its check-in month. Use it to compare volume across channels and spot seasonal patterns. Excludes cancelled."
            futureMonthsNote
          />
        </div>
        <p style={styles.subtitle}>Reservation volume by booking source</p>
      </div>
      <div ref={containerRef} style={styles.chartContainer}>
        {annotations && annotations.length > 0 && (
          <ChartAnnotations annotations={annotations} chartData={chartData} xKey="month" />
        )}
        {containerSize.width > 0 && containerSize.height > 0 && (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartDataWithMeta} 
            margin={{ top: 50, right: 30, left: 20, bottom: 5 }}
            style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: textColor }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12, fill: textColor }}
              tickFormatter={(value) => Math.round(value).toString()}
            />
            <Tooltip
              content={(props) => (
                <ChartTooltipPortal {...props} chartType="reservations" containerRef={containerRef} />
              )}
              cursor={{ fill: 'transparent' }}
              wrapperStyle={{ pointerEvents: 'none' }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: '20px',
                paddingBottom: isMobile ? '12px' : undefined,
                color: textColor,
              }}
              iconType="square"
            />
            {activeChannels.map((channelName) => (
              <Bar
                key={channelName}
                dataKey={channelName}
                stackId="reservations"
                fill={channelColors[channelName] || '#6B7280'}
                name={channelName}
                onClick={(data) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    handleBarClick(data.activePayload[0].payload);
                  }
                }}
              />
            ))}
            <Bar
              dataKey="_zero"
              stackId="reservations"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
            >
              <LabelList
                dataKey="total_value"
                position="top"
                formatter={formatTotalLabel}
                style={{ fill: '#111827', fontSize: 12, fontWeight: 700 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-5)',
    boxShadow: 'var(--shadow-md)',
    transition: 'var(--transition-base)',
  },
  header: {
    marginBottom: 'var(--spacing-5)',
  },
  title: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: 0,
    marginBottom: 'var(--spacing-1)',
  },
  subtitle: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
  },
  chartContainer: {
    height: '300px',
    minHeight: '300px',
    width: '100%',
    minWidth: '200px',
    position: 'relative',
    overflow: 'visible',
  },
  emptyState: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },
};

